/**
 * Technician background (what they bring) and observed evidence (what OptiBay
 * has learned) for ONE shop technician — two bounded reads, never one per
 * category. Writes go only through set_technician_trade_background.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import {
  categoryEvidenceView,
  type CategoryEvidenceView,
  type ExperienceLevel,
  type TradeBackgroundRow,
} from '@/lib/technicians/tradeBackground';

export const tradeBackgroundKey = (shopId: string | null | undefined, technicianId: string | null | undefined) =>
  ['technician-trade-background', shopId, technicianId] as const;

export function useTechnicianTradeBackground(shopId: string | null | undefined, technicianId: string | null | undefined) {
  return useQuery({
    queryKey: tradeBackgroundKey(shopId, technicianId),
    enabled: !!shopId && !!technicianId,
    retry: false,
    queryFn: async (): Promise<TradeBackgroundRow | null> => {
      const { data, error } = await (supabase as any)
        .from('technician_trade_background')
        .select('experience_level, years_professional, source, updated_at')
        .eq('shop_id', shopId).eq('technician_id', technicianId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as TradeBackgroundRow | null;
    },
  });
}

export function useTechnicianEvidenceStates(shopTechnicianId: string | null | undefined) {
  return useQuery({
    queryKey: ['technician-evidence-states', shopTechnicianId],
    enabled: !!shopTechnicianId,
    retry: false,
    queryFn: async (): Promise<CategoryEvidenceView[]> => {
      const { data, error } = await (supabase as any)
        .from('technician_category_authority')
        .select('category_key, score, baseline_score, baseline_class, baseline_prior_strength, evidence_count')
        .eq('technician_id', shopTechnicianId);
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>)
        .map((r) => categoryEvidenceView({
          categoryKey: String(r.category_key),
          score: r.score == null ? null : Number(r.score),
          baselineScore: r.baseline_score == null ? null : Number(r.baseline_score),
          baselineClass: (r.baseline_class as string) ?? null,
          baselinePriorStrength: r.baseline_prior_strength == null ? null : Number(r.baseline_prior_strength),
          evidenceCount: r.evidence_count == null ? 0 : Number(r.evidence_count),
        }))
        .sort((a, b) => a.categoryKey.localeCompare(b.categoryKey));
    },
  });
}

export interface SetTradeBackgroundArgs {
  shopId: string;
  /** null = "my own technician row in this shop". */
  technicianId: string | null;
  experienceLevel: ExperienceLevel | null;
  yearsProfessional: number | null;
}
export type SetTradeBackgroundOutcome = 'SAVED' | 'CLEARED' | 'NOT_MECHANICAL';

export async function setTechnicianTradeBackground(args: SetTradeBackgroundArgs): Promise<{ outcome: SetTradeBackgroundOutcome; technician_id: string }> {
  const { data, error } = await (supabase as any).rpc('set_technician_trade_background', {
    p_shop_id: args.shopId,
    p_technician_id: args.technicianId,
    p_experience_level: args.experienceLevel,
    p_years_professional: args.yearsProfessional,
  });
  if (error) throw error;
  return data as { outcome: SetTradeBackgroundOutcome; technician_id: string };
}

export function useSetTechnicianTradeBackground() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setTechnicianTradeBackground,
    onSuccess: (res, args) => {
      void qc.invalidateQueries({ queryKey: ['technician-trade-background', args.shopId] });
      if (res.outcome === 'NOT_MECHANICAL') {
        toast({ title: 'Not saved', description: 'Technician background applies to mechanical technicians only.', variant: 'destructive' });
      } else {
        toast({ title: res.outcome === 'CLEARED' ? 'Background cleared' : 'Background saved' });
      }
    },
    onError: (e: Error) => toast({ title: 'Background not saved', description: e.message, variant: 'destructive' }),
  });
}
